import { useId, useState } from "react";
import { BellRing, Eye, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useHostedLoopWatches } from "@/lib/loops/watches/product/hostedWatches";
import type { WatchKind } from "@/lib/loops/watches/watchCommand";
import type { Loop } from "@/lib/looseend/types";

/**
 * PEE v1.3 — "Keep an eye on this": user-confirmed watches on one Loop.
 *
 * Presentation only. The person chooses in plain language, sees exactly what
 * will happen, and confirms explicitly. A watch never changes the Loop; when it
 * fires, the Loop comes back into the Resolution Queue with the reason.
 */
export function LoopWatchPanel({ loop }: { loop: Loop }) {
  const watches = useHostedLoopWatches(loop.id);
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<WatchKind>("resurface_on");
  const [date, setDate] = useState("");
  const [condition, setCondition] = useState("");
  const groupId = useId();
  const closed = loop.status === "resolved" || loop.status === "archived";

  const confirm = async () => {
    const result = await watches.create({
      loopId: loop.id,
      kind,
      date: kind === "resurface_on" || kind === "still_open_by" ? date : null,
      condition: kind === "public_condition" ? condition : null,
    });
    if (!result.ok) {
      toast.error(result.message);
      return;
    }
    toast.success("Watching", { description: result.watch.label });
    setAdding(false);
    setDate("");
    setCondition("");
  };

  const stop = async (watchId: string) => {
    const result = await watches.stop(watchId);
    if (!result.ok) toast.error(result.message);
    else toast.success("Stopped watching");
  };

  const canConfirm =
    !watches.busy &&
    (kind === "resurface_on" || kind === "still_open_by"
      ? /^\d{4}-\d{2}-\d{2}$/.test(date)
      : true) &&
    (kind === "public_condition" ? condition.trim().length > 0 : true) &&
    (kind === "before_expected_date" ? Boolean(loop.expectedDate) : true);

  return (
    <section className="mt-5 border-t border-border pt-4" data-testid="loop-watch-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-foreground">Keep an eye on this</h3>
          <p className="text-xs text-muted-foreground">
            L∞seEnd can bring this back to you at the right moment. Nothing changes on its own — it
            just comes back with a reason.
          </p>
        </div>
        {!closed && !adding ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAdding(true)}
            data-testid="watch-add"
          >
            <Eye className="size-3.5" aria-hidden="true" />
            Watch
          </Button>
        ) : null}
      </div>

      {watches.phase === "ready" && watches.watches.length > 0 ? (
        <ul className="mt-3 grid gap-2" data-testid="watch-list">
          {watches.watches.map((watch) => (
            <li
              key={watch.id}
              className="flex items-start gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm"
            >
              {watch.status === "fired" ? (
                <BellRing className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
              ) : (
                <Eye className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-foreground">{watch.label}</p>
                {watch.status === "fired" && watch.firedReason ? (
                  <p className="text-xs text-muted-foreground">{watch.firedReason}</p>
                ) : null}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="-mr-1 size-7 shrink-0"
                aria-label="Stop watching"
                disabled={watches.busy}
                onClick={() => void stop(watch.id)}
              >
                <X className="size-4" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {watches.phase === "unavailable" ? (
        <p className="mt-2 text-xs text-muted-foreground">Couldn’t load watches just now.</p>
      ) : null}

      {adding ? (
        <div
          className="mt-3 grid gap-3 rounded-lg border border-border bg-card p-3"
          data-testid="watch-form"
        >
          <RadioGroup
            value={kind}
            onValueChange={(v) => setKind(v as WatchKind)}
            className="grid gap-2"
          >
            <label htmlFor={`${groupId}-a`} className="flex items-start gap-2 text-sm">
              <RadioGroupItem id={`${groupId}-a`} value="resurface_on" className="mt-0.5" />
              <span>Bring this back on a date</span>
            </label>
            <label
              htmlFor={`${groupId}-b`}
              className={`flex items-start gap-2 text-sm ${loop.expectedDate ? "" : "opacity-50"}`}
            >
              <RadioGroupItem
                id={`${groupId}-b`}
                value="before_expected_date"
                className="mt-0.5"
                disabled={!loop.expectedDate}
              />
              <span>
                Remind me the day before it’s expected
                {loop.expectedDate ? "" : " (no expected date recorded)"}
              </span>
            </label>
            <label htmlFor={`${groupId}-c`} className="flex items-start gap-2 text-sm">
              <RadioGroupItem id={`${groupId}-c`} value="still_open_by" className="mt-0.5" />
              <span>Check in if it’s still open by a date</span>
            </label>
            <label htmlFor={`${groupId}-d`} className="flex items-start gap-2 text-sm">
              <RadioGroupItem id={`${groupId}-d`} value="public_condition" className="mt-0.5" />
              <span>Watch for a public change (checked once a day, for up to 60 days)</span>
            </label>
          </RadioGroup>

          {kind === "resurface_on" || kind === "still_open_by" ? (
            <div className="grid gap-1">
              <Label htmlFor={`${groupId}-date`} className="text-xs">
                Which day?
              </Label>
              <Input
                id={`${groupId}-date`}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="max-w-[200px]"
              />
            </div>
          ) : null}
          {kind === "public_condition" ? (
            <div className="grid gap-1">
              <Label htmlFor={`${groupId}-cond`} className="text-xs">
                What public change should it look for?
              </Label>
              <Input
                id={`${groupId}-cond`}
                value={condition}
                maxLength={200}
                placeholder="e.g. the 2026 passport fee has been published"
                onChange={(e) => setCondition(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground">
                Public information only — no names, account or reference numbers. What it finds is
                information, not proof.
              </p>
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAdding(false)}
              disabled={watches.busy}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => void confirm()}
              disabled={!canConfirm}
              data-testid="watch-confirm"
            >
              Start watching
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
